import { Component } from "react";
import PropTypes from "prop-types";
import { Card, Table, Badge, Spinner, Alert, Button } from "react-bootstrap";
import { FaFilePdf, FaExternalLinkAlt } from "react-icons/fa";
import api from "../../services/api";
import { formataDataHoraExibicao } from "../../common/utils";

/**
 * Indicações ligadas a este cadastro, nos dois sentidos:
 *
 *  - `papel="medico"`   → profissionais que ELE indicou (cadastro do médico);
 *  - `papel="indicado"` → médicos que indicaram ELE (cadastro do profissional).
 *
 * Inclui as encerradas: o escopo pede que o histórico fique preservado.
 *
 * A aba só é montada em programa que oferece indicação; quem decide isso é a tela de cadastro.
 */
class PrestadorIndicacoes extends Component {
    constructor(props) {
        super(props);
        this.state = { indicacoes: [], carregando: true, erro: "" };
    }

    componentDidMount() {
        this.carregar();
    }

    componentDidUpdate(anterior) {
        if (anterior.prestadorId !== this.props.prestadorId ||
            anterior.programaId !== this.props.programaId) {
            this.carregar();
        }
    }

    carregar = async () => {
        const { prestadorId, programaId } = this.props;
        if (!prestadorId) {
            this.setState({ carregando: false, indicacoes: [] });
            return;
        }

        this.setState({ carregando: true, erro: "" });
        try {
            const campo = this.props.papel === "indicado" ? "indicado_id" : "medico_id";
            const params = [`${campo}=${prestadorId}`, "limit=100"];
            if (programaId) params.push(`programa_id=${programaId}`);
            const { data } = await api.get(`/pessoas/indicacoes-profissionais/?${params.join("&")}`);
            this.setState({ carregando: false, indicacoes: data.results || [] });
        } catch (error) {
            const detalhe = error.response?.data?.detail;
            this.setState({
                carregando: false,
                erro: detalhe || "Não foi possível carregar as indicações deste cadastro.",
            });
        }
    };

    renderSituacao = (i) => {
        if (i.status === "cancelada") return <Badge bg="secondary">Encerrada</Badge>;
        if (i.status === "rascunho") return <Badge bg="warning">Aguardando assinatura</Badge>;
        if (!i.dados_indicado) return <Badge bg="info">Aguardando cadastro</Badge>;
        return <Badge bg="success">Ativa</Badge>;
    };

    render() {
        const { indicacoes, carregando, erro } = this.state;
        const doIndicado = this.props.papel === "indicado";

        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <h6 className="text-muted">{doIndicado
                        ? "Médicos que indicaram este profissional"
                        : "Profissionais indicados por este médico"}</h6>
                    <p className="text-muted small">
                        Inclui as indicações encerradas — o histórico é preservado.
                    </p>

                    {carregando ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : erro ? (
                        <Alert variant="danger" className="mb-0">{erro}</Alert>
                    ) : indicacoes.length === 0 ? (
                        <p className="text-muted mb-0">{doIndicado
                            ? "Este profissional ainda não foi indicado por nenhum médico."
                            : "Este médico ainda não indicou nenhum profissional."}</p>
                    ) : (
                        <Table responsive className="custom-table">
                            <thead>
                                <tr>
                                    <th>{doIndicado ? "Médico que indicou" : "Profissional indicado"}</th>
                                    <th>{doIndicado ? "CRM" : "Conselho"}</th>
                                    <th>Cadastro</th>
                                    <th>Indicado em</th>
                                    <th>Situação</th>
                                    <th>Termo</th>
                                    <th className="text-end">Declaração</th>
                                </tr>
                            </thead>
                            <tbody>
                                {indicacoes.map((i) => (
                                    <tr key={i.id}>
                                        <td>{doIndicado ? (i.dados_medico?.nome || "-") : i.nome_indicado}</td>
                                        <td>
                                            {doIndicado
                                                ? [i.dados_medico?.registro, i.dados_medico?.registro_uf]
                                                    .filter(Boolean).join("/") || "-"
                                                : [i.conselho, i.registro, i.registro_uf].filter(Boolean).join(" ") || "-"}
                                        </td>
                                        <td>
                                            {/* leva direto ao cadastro: sem isto é preciso ir procurar em Provedores */}
                                            {doIndicado ? (
                                                i.dados_medico ? (
                                                    <a href={`/medicos/edit/${i.dados_medico.id}`}
                                                       target="_blank" rel="noopener noreferrer">
                                                        {i.dados_medico.nome} <FaExternalLinkAlt size={10} />
                                                    </a>
                                                ) : <span className="text-muted small">—</span>
                                            ) : i.dados_indicado ? (
                                                <a href={`/prestadores/edit/${i.dados_indicado.id}`}
                                                   target="_blank" rel="noopener noreferrer">
                                                    {i.dados_indicado.nome} <FaExternalLinkAlt size={10} />
                                                </a>
                                            ) : <span className="text-muted small">não vinculado</span>}
                                        </td>
                                        <td>{i.data_indicacao ? formataDataHoraExibicao(i.data_indicacao) : "-"}</td>
                                        <td>{this.renderSituacao(i)}</td>
                                        <td>
                                            {!i.data_aceite_termo ? (
                                                <span className="text-muted small">—</span>
                                            ) : i.termo_comprovante_url ? (
                                                <a href={i.termo_comprovante_url} target="_blank"
                                                   rel="noopener noreferrer" title="Abrir o termo assinado">
                                                    <Badge bg="success">Aceito</Badge>
                                                </a>
                                            ) : <Badge bg="success">Aceito</Badge>}
                                        </td>
                                        <td className="text-end">
                                            {i.declaracao_url ? (
                                                <Button variant="light" size="sm" href={i.declaracao_url}
                                                        target="_blank" rel="noopener noreferrer">
                                                    <FaFilePdf className="me-1" /> Ver
                                                </Button>
                                            ) : <span className="text-muted small">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        );
    }
}

PrestadorIndicacoes.propTypes = {
    prestadorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    programaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    //"medico" (padrão): quem ele indicou. "indicado": quem indicou ele.
    papel: PropTypes.oneOf(["medico", "indicado"]),
};

export default PrestadorIndicacoes;
